(() => {
  'use strict';
  const gate = document.getElementById('kaia-christmas-gate');
  if (!gate || gate.dataset.bound === 'true') return;
  gate.dataset.bound = 'true';
  const form = gate.querySelector('form');
  const input = gate.querySelector('input[name="code"]');
  const button = form.querySelector('button[type="submit"]');
  const label = button.querySelector('span');
  const status = gate.querySelector('[role="status"]');
  const attributeName = '_kaia_christmas_access';
  const initialLabel = label.textContent;
  let busy = false;
  const localURL = (value) => {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin || url.search || url.hash) throw new Error('Invalid local endpoint');
    return url.pathname;
  };
  let unlockURL;
  let cartURL;
  let campaignURL;
  try {
    unlockURL = localURL(gate.dataset.unlockUrl);
    cartURL = localURL(gate.dataset.cartUpdateUrl);
    campaignURL = new URL(localURL(gate.dataset.campaignUrl), window.location.origin);
    campaignURL.searchParams.set('collection', gate.dataset.collection);
  } catch {
    status.textContent = 'We cannot open the collection right now. Please try again shortly.';
    return;
  }
  const say = (message, isError = false) => {
    status.textContent = message;
    status.classList.toggle('error', isError);
  };
  const postJSON = async (url, payload) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, {
        method: 'POST', credentials: 'same-origin', redirect: 'error', cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload), signal: controller.signal,
      });
      const data = await response.json().catch(() => ({}));
      return { response, data };
    } finally { clearTimeout(timer); }
  };
  // The proxy is the clock/phase authority; browser time only schedules a check.
  // This refresh supplements, and cannot replace, the scheduled theme release.
  let releaseTimer;
  let releaseCheckAt = Infinity;
  let lastReleaseCheck = -Infinity;
  let checkingRelease = false;
  let releaseStopped = false;
  const clearReleaseTimer = () => {
    clearTimeout(releaseTimer);
    releaseTimer = undefined;
    releaseCheckAt = Infinity;
  };
  const queueReleaseCheck = (delay) => {
    if (releaseStopped || document.hidden) return;
    const wait = Math.max(1000, Math.min(delay, 300000));
    const at = performance.now() + wait;
    if (at >= releaseCheckAt) return;
    clearReleaseTimer();
    releaseCheckAt = at;
    releaseTimer = setTimeout(() => { clearReleaseTimer(); checkRelease(); }, wait);
  };
  const publicRefresh = (data) => {
    releaseStopped = true;
    clearReleaseTimer();
    input.value = '';
    // Only a nonsecret, campaign-scoped reload marker is stored. Without working
    // session storage, require an explicit refresh rather than risk a reload loop.
    const key = `kaia:public-release:${data.campaignId}:${data.revision}:${gate.dataset.collection}`;
    try {
      if (sessionStorage.getItem(key) !== 'reloaded') {
        sessionStorage.setItem(key, 'reloaded');
        if (sessionStorage.getItem(key) === 'reloaded') {
          window.location.reload(); // Retains locale, filters, sorting, pagination and hash.
          return;
        }
      }
    } catch { /* Storage can be unavailable in privacy-restricted browsers. */ }
    say('Christmas is open to everyone. ');
    const refresh = document.createElement('a');
    refresh.href = window.location.href;
    refresh.textContent = 'Refresh the collection';
    status.append(refresh);
  };
  const checkRelease = async (onReturn = false) => {
    if (releaseStopped || document.hidden) return;
    if (checkingRelease) return;
    if (busy) { queueReleaseCheck(15000); return; }
    const sinceLast = performance.now() - lastReleaseCheck;
    if (onReturn && sinceLast < 15000) {
      if (releaseTimer === undefined) queueReleaseCheck(15000 - sinceLast);
      return;
    }
    checkingRelease = true;
    lastReleaseCheck = performance.now();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(campaignURL.href, {
        credentials: 'same-origin', redirect: 'error', cache: 'no-store',
        headers: { Accept: 'application/json' }, signal: controller.signal,
      });
      const data = await response.json();
      if (!response.ok || !['locked', 'scheduled', 'public'].includes(data.phase)
          || !/^[a-z0-9][a-z0-9_-]{0,79}$/.test(data.campaignId ?? '')
          || !Number.isSafeInteger(data.revision) || data.revision < 1
          || data.cartAttribute !== attributeName || data.collection?.handle !== gate.dataset.collection
          || (data.publicAt !== null && (typeof data.publicAt !== 'string' || !Number.isFinite(Date.parse(data.publicAt))))) {
        throw new Error('Invalid campaign response');
      }
      if (data.phase === 'public') {
        if (busy || document.hidden) { queueReleaseCheck(15000); return; }
        publicRefresh(data);
        return;
      }
      const untilPublic = data.publicAt === null ? NaN : Date.parse(data.publicAt) - Date.now();
      queueReleaseCheck(untilPublic > 0 ? untilPublic + 100 : 30000);
    } catch {
      // A failed status check neither opens merchandise nor interrupts code entry.
      queueReleaseCheck(30000);
    } finally {
      clearTimeout(timeout);
      checkingRelease = false;
    }
  };
  button.disabled = false;
  input.addEventListener('input', () => input.removeAttribute('aria-invalid'));
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (busy || !form.reportValidity()) return;
    busy = true;
    input.disabled = true;
    button.disabled = true;
    input.removeAttribute('aria-invalid');
    label.textContent = 'Unlocking…';
    say('');
    try {
      const { response, data } = await postJSON(unlockURL, { collection: gate.dataset.collection, code: input.value });
      if (!response.ok) {
        if (response.status === 403 && data.error === 'not_started') {
          say('Your Christmas early access has not started yet. Please check the time in your invitation.', true);
        } else if (response.status === 403 && data.error === 'incorrect_code') {
          input.setAttribute('aria-invalid', 'true');
          say('That code does not look quite right. Please check your Lush email and try again.', true);
        } else if (response.status === 429) {
          say('Please wait a moment before trying your code again.', true);
        } else {
          say('We cannot open the collection right now. Please try again shortly.', true);
        }
        return;
      }
      if (data.phase === 'public') {
        input.value = '';
        window.location.reload();
        return;
      }
      if (data.phase !== 'unlocked' || data.cartAttribute !== attributeName || data.collection?.handle !== gate.dataset.collection || typeof data.token !== 'string' || !/^v1\|lush-south-africa\.myshopify\.com\|[a-z0-9_-]+\|[1-9][0-9]*\|[1-9][0-9]*\|[a-f0-9]{32}\|[a-f0-9]{64}$/.test(data.token)) {
        throw new Error('Invalid unlock response');
      }
      input.value = '';
      // Shopify merges the single submitted attribute; no items, notes or other attributes are sent.
      const cart = await postJSON(cartURL, { attributes: { [attributeName]: data.token } });
      if (!cart.response.ok || cart.data.attributes?.[attributeName] !== data.token) throw new Error('Cart access was not saved');
      say('Christmas is unlocked. Opening the collection…');
      window.location.reload(); // Retains the exact selected collection, filters, sort and pagination.
    } catch {
      say('We cannot open the collection right now. Please try again shortly.', true);
    } finally {
      busy = false;
      input.disabled = false;
      button.disabled = false;
      label.textContent = initialLabel;
      if (status.classList.contains('error')) input.focus();
    }
  });
  window.addEventListener('pagehide', () => { input.value = ''; clearReleaseTimer(); });
  window.addEventListener('pageshow', (event) => {
    // Preserve native cart/grant readback when navigating back to a pre-unlock
    // cached document. A known public cached gate has already used its budget.
    if (event.persisted && !releaseStopped) { window.location.reload(); return; }
    checkRelease(true);
  });
  window.addEventListener('focus', () => checkRelease(true));
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) clearReleaseTimer();
    else checkRelease(true);
  });
  checkRelease();
})();
